import type {
  BrandId,
  OfficialStatusProvidersResponse,
  PublicStatusResponse,
  TenantCodexCapacityResponse,
} from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Brand this console pins on every status query. `BrandId` is the registry
 * union from `packages/contracts/src/brands.ts`, so a typo fails the build
 * instead of shipping a query string the platform silently folds to the
 * default brand.
 */
const STATUS_BRAND: BrandId = 'nxtpath';

/**
 * Public service status for the Nxtpath brand.
 *
 * `brand` is a registry id, not a display name — the tenant console always
 * pins its own brand so the response never mixes another tenant surface's
 * namespaces in.
 */
export async function getNxtpathPublicStatus(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<PublicStatusResponse> {
  return platformApiRequest<PublicStatusResponse>(
    `/v1/status?brand=${STATUS_BRAND}`,
    { signal },
    fetchImpl,
  );
}

export async function getPlatformHealth(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<{ readonly ok: true }> {
  return platformApiRequest<{ readonly ok: true }>('/v1/health', { signal }, fetchImpl);
}

/**
 * Tenant Codex at-capacity series. Separate from {@link getNxtpathPublicStatus}:
 * this is not the public `/v1/status` feed and must not be folded into that
 * request. Cookie-auth, same channel as `/v1/usage/*`.
 */
export async function getCodexCapacity(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<TenantCodexCapacityResponse> {
  return platformApiRequest<TenantCodexCapacityResponse>(
    '/v1/status/codex-capacity',
    { signal },
    fetchImpl,
  );
}

/**
 * Official OpenAI and Anthropic status pages, mirrored by platform.
 *
 * Separate from {@link getNxtpathPublicStatus}: this is not the public
 * `/v1/status` feed and must not be folded into that request. Cookie-auth,
 * same channel as {@link getCodexCapacity}. The backend refreshes about
 * once a minute.
 */
export async function getOfficialProviderStatus(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OfficialStatusProvidersResponse> {
  return platformApiRequest<OfficialStatusProvidersResponse>(
    '/v1/status/providers',
    { signal },
    fetchImpl,
  );
}
